import { atom, useAtom } from "jotai";
import { atomWithStorage, atomFamily, useAtomValue } from "jotai/utils";
import deepEqual from "fast-deep-equal";

import { DataFile } from "interfaces";

import { fileColumns, deducedTypes } from "./file-api";

interface Spec {
  fileId: string;
  column: string;
}

export const columnType = atomFamily(({ fileId, column }: Spec) => {
  const typeAtom = atomWithStorage<DataFile.ColType>(
    `data-files/column-type/${fileId}/${column}`,
    null
  );

  return atom<DataFile.ColType, DataFile.ColType>(
    (get) => {
      const setType = get(typeAtom);
      if (setType) {
        return setType;
      }
      const deduced = get(deducedTypes(fileId))[column];
      if (deduced === "int" || deduced === "float") {
        return "numeric";
      } else {
        return deduced;
      }
    },
    (get, set, value) => {
      set(typeAtom, value);
    }
  );
}, deepEqual);

export const useColumnType = (fileId: string, column: string) =>
  useAtom(columnType({ fileId, column }));

export const useGetColumnType = (fileId: string, column: string) =>
  useAtomValue(columnType({ fileId, column }));

export const fileColTypes = atomFamily((fileId: string) =>
  atom<DataFile.UserDefinedTypes, DataFile.UserDefinedTypes>(
    (get) => {
      const columns = get(fileColumns(fileId));
      return Object.fromEntries(
        (columns || []).map((c) => [c, get(columnType({ fileId, column: c }))])
      );
    },
    (get, set, values) => {
      Object.entries(values).forEach(([column, colType]) => {
        set(columnType({ fileId, column }), colType);
      });
    }
  )
);

export const useGetTypesForFile = (fileId: string) => {
  return useAtomValue(fileColTypes(fileId));
};
